import React, { useCallback, useEffect, useState } from 'react';

import { Button, CircularProgress, Dialog, DialogActions, SelectChangeEvent, Typography, useTheme } from '@mui/material';

import { AccordionSection } from '~/components/AccordionSection';
import { useUpdateVehicleOptions, useVehicleOptions } from '~/contexts/VehicleOptionsContext';
import { IConfigurationOptionInteriorColors } from '~/domain/IConfigurationOptionInteriorColors';
import { IConfigurationSuggestion } from '~/domain/IConfigurationSuggestion';
import { IConfigurationTrimColor } from '~/domain/IConfigurationTrim';
import { ConfigurationSuggestionDialog } from '~/pages/RequestPage/ConfigurationSuggestionDialog';

import { ConfigurationPackSetColors } from './ConfigurationPackSetColors';
import { ConfigurationPackSetInteriorColors } from './ConfigurationPackSetInteriorColors';

interface IConfigurationOptionPropsColors {
  name: string;
  code: string;
  client: string | undefined;
  trim: string;
  colors: IConfigurationTrimColor[];
}

interface IConfigurationSuggestions {
  suggestChanges: IConfigurationSuggestion[];
  featuresOnAccept: string[];
}

export const ConfigurationOptionColors: React.FC<IConfigurationOptionPropsColors> = (props) => {

  const { name, colors, trim } = props;

  const { palette } = useTheme();

  const vehicleOptions = useVehicleOptions();
  const updateVehicleOptions = useUpdateVehicleOptions();
  const { features } = vehicleOptions;
  const [{ suggestChanges = [], featuresOnAccept = [] } = {}, setConfigurationSuggestions] = useState<IConfigurationSuggestions | undefined>();
  const [suggestionWorker, setSuggestionWorker] = useState<Worker>();

  const [isComputingSuggestion, setIsComputingSuggestion] = useState<boolean>(false);
  const [currentTrim, setCurrentTrim] = useState<string>('');
  const [currentSelected, setCurrentSelected] = useState<string>(colors[0].code);
  const [currentInteriorValueSelected, setCurrentInteriorValueSelected] = useState<string>(colors[0].packs[0].code);
  const [newPackState, setNewPackState] = useState<IConfigurationOptionInteriorColors>(colors[0]);

  const selection = colors.find(x => currentSelected.includes(x.code));

  const onCancelSuggestionWorker = useCallback(() => {
    suggestionWorker?.terminate();
    setIsComputingSuggestion(false);
    setSuggestionWorker(undefined);
  }, [suggestionWorker]);

  const resetFeaturesArray = useCallback((currentSelected: string, currentInteriorValueSelected: string, features: string[], colors: IConfigurationTrimColor[]) => {
    //alert('dude!');
    //alert(1);
    // console.log('resetFeaturesArray trim features: ', features)
    // console.log('resetFeaturesArray trim currentSelected: ', currentSelected)
    // console.log('resetFeaturesArray trim selectedColor: ', selectedColor)
    // console.log('resetFeaturesArray trim colors: ', colors)
    // console.log('resetFeaturesArray trim colors: ', colors)
    const index = features.indexOf(currentSelected);
    
    setCurrentSelected(currentSelected);
    
    //const newIndex = colors.findIndex(x => x.code === selectedColor);
    //console.log('resetFeaturesArray trim newIndex: ', newIndex)

    //if (index === -1 && currentSelected === colors[0].code) {
    if (index === -1 && currentSelected === colors[0].code) {
      const newFeatures = [...features];
      //console.log('resetFeaturesArray newFeatures: ', newFeatures)

      newFeatures.push(colors[0].code);
      newFeatures.push(colors[0].packs[0].code);
      //save to features
      updateVehicleOptions({
        ...vehicleOptions,
        features: newFeatures,
      });
    }

  }, [updateVehicleOptions, vehicleOptions]);

  const resetTrim = useCallback((trim: string) => {
    
    if (trim !== currentTrim) {
      let newIndex = colors.findIndex(x => x.code === currentSelected);
      //console.log('resetTrim newIndex: ', newIndex);
      //console.log('resetTrim currentSelected: ', currentSelected);
      //console.log('resetTrim colors[0]: ', colors[0]);
      if(newIndex < 0){
        newIndex = 0;
      }
      else{
        const newFeatures = [...features];
        const arrFix = [];
        arrFix.push(newFeatures[0]);
        arrFix.push(currentSelected);
        arrFix.push(colors[newIndex].packs[0].code);
        
        updateVehicleOptions({
          ...vehicleOptions,
          features: arrFix,
        });
      }
      
      setCurrentSelected(colors[newIndex].code);
      setCurrentInteriorValueSelected(colors[newIndex].packs[0].code);
      setNewPackState(colors[newIndex]);
      
      setCurrentTrim(trim);
    }

  }, [updateVehicleOptions, vehicleOptions, colors, currentTrim, features, currentSelected]);

  useEffect(() => {
    resetFeaturesArray(currentSelected, currentInteriorValueSelected, features, colors);
  });

  useEffect(() => {
    if (trim !== currentTrim) {
 
      //console.log('currentTrim trim: ', trim);
      resetTrim(trim);
    }

  }, [trim, resetTrim, colors, currentTrim]);

  const handleChange = useCallback((e: SelectChangeEvent) => {
    const code = e.target.value;
    
    setCurrentSelected(code);
   
    const index = features.indexOf(selection?.code || '');
    const newPack = colors.find(x => x.code === code);
    const oldPack = colors.find(x => x.code === features[index]);
    //setCurrentSelected(code);
    const newFeatures = [...features];

    //remove old interior codes
    if (oldPack) {
      const indexInt = newFeatures.indexOf(currentInteriorValueSelected);
      if (indexInt !== -1) {
        newFeatures.splice(indexInt, 1);
      }
    }

    //set interior packs into state
    if (newPack) {
      setCurrentInteriorValueSelected(newPack.packs[0].code);
      setNewPackState(newPack);
    }

    if (index > -1 && features[index] !== code && newPack && oldPack) {
      newFeatures[index] = code;
      newFeatures.push(newPack.packs[0].code);

      updateVehicleOptions({
        ...vehicleOptions,
        features: newFeatures,
      });
    }
    else {
      newFeatures.push(code);
      updateVehicleOptions({
        ...vehicleOptions,
        features: newFeatures,
      });
    }

  }, [features, colors, selection, updateVehicleOptions, vehicleOptions, currentInteriorValueSelected]);

  const handleInteriorChange = useCallback((e: SelectChangeEvent) => {

    const code = e.target.value;
    const index = features.indexOf(currentInteriorValueSelected || '');
    const newFeatures = [...features];

    if (index === -1) {
      newFeatures.push(code);
    }
    else {
      newFeatures[index] = code;
    }

    setCurrentInteriorValueSelected(code);

    updateVehicleOptions({
      ...vehicleOptions,
      features: newFeatures,
    });

  }, [features, currentInteriorValueSelected, updateVehicleOptions, vehicleOptions]);

  return (
    <>
      {/* suppress dialog code for now */}
      <Dialog
        open={isComputingSuggestion}
        PaperProps={{
          sx: {
            width: 570,
            maxHeight: 544,
            p: '34px 40px 40px 40px',
            alignItems: 'center',
          },
        }}
      >
        <Typography>Trying to find a suitable suggestion...</Typography>
        <CircularProgress />
        <DialogActions>
          <Button autoFocus color="dark" size="small" onClick={onCancelSuggestionWorker}>Cancel</Button>
        </DialogActions>
      </Dialog>

      {/* create accordian section for each trim from parent map */}

      {/* create child accordian section for trim color */}
      <AccordionSection
        contenttype={'colors'}
        label={
          <>
            <span style={{ color: palette.primary.main, marginRight: 10 }}>
              &mdash; Colors
            </span>
          </>
        }
      >

        <ConfigurationPackSetColors
          label={name}
          onChange={handleChange}
          packs={colors}
          value={currentSelected}
        />
      </AccordionSection>

      <AccordionSection
        contenttype={'interiors'}
        label={
          <>
            <span style={{ color: palette.primary.main, marginRight: 10 }}>
              &mdash; Interior Colors
            </span>
          </>
        }
      >

        <ConfigurationPackSetInteriorColors
          label={name}
          onChange={handleInteriorChange}
          packs={[newPackState]}
          value={currentInteriorValueSelected}
        />

      </AccordionSection>

      {suggestChanges.length > 0 && (
        <ConfigurationSuggestionDialog
          suggestChanges={suggestChanges}
          onCancel={() => {
            setConfigurationSuggestions(undefined);
          }}
          onAccept={() => {
            updateVehicleOptions({
              ...vehicleOptions,
              features: featuresOnAccept,
            });
            setConfigurationSuggestions(undefined);
          }}
        />
      )}

    </>
  );
};
